/** The settings panel: the Google account, and the three deck preferences. */

import { type Component, Match, Show, Switch as SwitchFlow } from 'solid-js';
import { showLibrary } from '../../app/browse.ts';
import { toggleCinematic, toggleCrackle, togglePictureDisc } from '../../app/deck-commands.ts';
import { messageOf } from '../../app/playback.ts';
import { account, lib } from '../../state/library.ts';
import { prefs } from '../../state/prefs.ts';
import { setModal, toast } from '../../state/ui.ts';
import { Google, Key, SignOut } from '../icons/account.tsx';
import { Button, Switch } from './Controls.tsx';

/* Both of these announce themselves through the library's own events, which
   is where the account state and the query cache are brought back in step. */
async function signIn(): Promise<void> {
  try {
    toast('Opening Google sign-in…', 1600);
    const profile = await lib.signIn();
    setModal(null);
    toast(`Signed in as ${profile.name}`);
    showLibrary();
  } catch (e) {
    toast(messageOf(e, 'Sign-in failed'));
  }
}

function signOut(): void {
  lib.signOut();
  setModal(null);
  toast('Signed out');
}

const AccountSection: Component = () => (
  <SwitchFlow>
    <Match when={account().signedIn}>
      <p>
        Signed in as <strong>{account().name ?? 'your Google account'}</strong>.
      </p>
      <Button variant="quiet" onClick={signOut}>
        <SignOut />
        <span>Sign out</span>
      </Button>
    </Match>

    <Match when={account().configured}>
      <Button onClick={() => void signIn()}>
        <Google />
        <span>Sign in with Google</span>
      </Button>
      <p style={{ 'margin-top': '12px' }}>
        Opens Google's consent screen. Read-only access to your YouTube library.
      </p>
    </Match>

    <Match when={!account().configured}>
      <p>No Google credentials yet, so the app is running six generated demo records.</p>
      <Button onClick={() => setModal('keys')}>
        <Key />
        <span>Use my own keys</span>
      </Button>
    </Match>
  </SwitchFlow>
);

export const Settings: Component = () => (
  <>
    <h3>Account</h3>
    <AccountSection />
    <Show when={account().configured}>
      <Button variant="quiet" onClick={() => setModal('keys')}>
        <Key />
        <span>Edit API keys</span>
      </Button>
    </Show>

    <h3>Deck</h3>
    <Switch
      label="Cinematic record change"
      description="Lift, swap and cue the next record"
      checked={prefs.cinematicSwap}
      onChange={toggleCinematic}
    />
    <Switch
      label="Picture disc"
      description="Print the album art across the whole record"
      checked={prefs.pictureDisc}
      onChange={togglePictureDisc}
    />
    <Switch
      label="Vinyl crackle"
      description="Surface noise under demo records"
      checked={prefs.crackle}
      onChange={toggleCrackle}
    />
  </>
);
